import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, FileText, TrendingUp, Calendar, Download, CreditCard, Search, AlertCircle, Phone, Users } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import LojaSelector from "@/components/LojaSelector";
import LoadingSpinner from "@/components/LoadingSpinner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_TOOLTIP_STYLE = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 13 };
const LINE_DOT_STYLE = { fill: "#10B981", r: 4, strokeWidth: 0 };
const LINE_ACTIVE_DOT_STYLE = { r: 6 };

const Dashboard = () => {
  const [lojaSelecionada, setLojaSelecionada] = useState("Todas as lojas");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("semana");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [showDateRange, setShowDateRange] = useState(false);
  const [pendentes, setPendentes] = useState(null);
  const [origemCanais, setOrigemCanais] = useState(null);

  const carregarPendentes = useCallback(async () => {
    try {
      const params = lojaSelecionada !== "Todas as lojas" ? { loja: lojaSelecionada } : {};
      const res = await axios.get(`${API}/dashboard/pendentes-abertos`, { params });
      setPendentes(res.data);
    } catch {
      setPendentes(null);
    }
  }, [lojaSelecionada]);

  useEffect(() => { carregarPendentes(); }, [carregarPendentes]);

  const calcularPeriodo = useCallback((tipo) => {
    const hoje = new Date();
    hoje.setHours(23, 59, 59, 999);
    const fim = new Date(hoje);
    const inicio = new Date(hoje);
    inicio.setHours(0, 0, 0, 0);
    if (tipo === "semana") inicio.setDate(hoje.getDate() - 7);
    else if (tipo === "mes") inicio.setMonth(hoje.getMonth() - 1);
    return { start: inicio.toISOString(), end: fim.toISOString() };
  }, []);

  const getDatas = useCallback(() => {
    if (showDateRange && dataInicio && dataFim) {
      return {
        start: new Date(dataInicio + "T00:00:00").toISOString(),
        end: new Date(dataFim + "T23:59:59").toISOString(),
      };
    }
    return calcularPeriodo(periodo);
  }, [showDateRange, dataInicio, dataFim, periodo, calcularPeriodo]);

  const carregarStats = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getDatas();
      const response = await axios.get(`${API}/dashboard/stats`, {
        params: { start_date: start, end_date: end },
      });
      let data = response.data;

      if (lojaSelecionada !== "Todas as lojas") {
        const lojaData = data.vendas_por_loja?.find((l) => l.loja === lojaSelecionada);
        const pagFiltrado = {};
        const recRes = await axios.get(`${API}/receitas`, {
          params: { start_date: start, end_date: end },
        });
        const filtradas = recRes.data.filter((r) => r.nome_loja === lojaSelecionada);
        const diaDict = {};
        filtradas.forEach((r) => {
          const d = new Date(r.data_registro).toISOString().split("T")[0];
          if (!diaDict[d]) diaDict[d] = { data: d, valor: 0, quantidade: 0 };
          diaDict[d].valor += r.valor_total || 0;
          diaDict[d].quantidade += 1;
          const f = r.forma_pagamento || "Nao informado";
          if (!pagFiltrado[f]) pagFiltrado[f] = { forma_pagamento: f, valor: 0, quantidade: 0 };
          pagFiltrado[f].valor += r.valor_total || 0;
          pagFiltrado[f].quantidade += 1;
        });
        data = {
          ...data,
          total_vendas: lojaData?.valor || 0,
          total_receitas: lojaData?.quantidade || 0,
          media_venda: lojaData ? (lojaData.valor / (lojaData.quantidade || 1)) : 0,
          vendas_por_dia: Object.values(diaDict).sort((a, b) => a.data.localeCompare(b.data)),
          vendas_por_forma_pagamento: Object.values(pagFiltrado).sort((a, b) => b.valor - a.valor),
        };
      }
      setStats(data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [getDatas, lojaSelecionada]);

  useEffect(() => { carregarStats(); }, [carregarStats]);

  const carregarOrigemCanais = useCallback(async () => {
    try {
      const { start, end } = getDatas();
      const params = { start_date: start, end_date: end };
      if (lojaSelecionada !== "Todas as lojas") params.loja = lojaSelecionada;
      const res = await axios.get(`${API}/dashboard/origem-canais`, { params });
      setOrigemCanais(res.data);
    } catch { setOrigemCanais(null); }
  }, [getDatas, lojaSelecionada]);

  useEffect(() => { carregarOrigemCanais(); }, [carregarOrigemCanais]);

  const aplicarDateRange = () => {
    if (!dataInicio || !dataFim) return toast.error("Selecione datas de inicio e fim");
    if (new Date(dataInicio) > new Date(dataFim)) return toast.error("Data inicio deve ser anterior");
    setShowDateRange(true);
    setPeriodo("");
    carregarStats();
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const exportarCSV = async () => {
    try {
      const { start, end } = getDatas();
      const res = await axios.get(`${API}/receitas`, { params: { start_date: start, end_date: end } });
      let dados = res.data;
      if (lojaSelecionada !== "Todas as lojas") dados = dados.filter((r) => r.nome_loja === lojaSelecionada);
      const csv = [
        ["Data", "OS", "Cliente", "Loja", "Valor", "Pagamento", "Parcelas"],
        ...dados.map((r) => [
          new Date(r.data_registro).toLocaleDateString("pt-BR"), r.os || "", r.nome_cliente || "",
          r.nome_loja || "", r.valor_total || "", r.forma_pagamento || "", r.parcelas || "",
        ]),
      ].map((row) => row.join(";")).join("\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `vendas_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      toast.success("CSV exportado!");
    } catch (e) { toast.error("Erro ao exportar"); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Painel Operacional</h1>
        <p className="text-sm text-slate-500 mt-1">Vendas e faturamento em tempo real</p>
      </div>

      {/* Filtros */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          {[
            { key: "hoje", label: "Hoje" },
            { key: "semana", label: "7 dias" },
            { key: "mes", label: "30 dias" },
          ].map((p) => (
            <Button key={p.key} size="sm"
              variant={periodo === p.key && !showDateRange ? "default" : "outline"}
              onClick={() => { setPeriodo(p.key); setShowDateRange(false); }}
              data-testid={`filter-${p.key}`}
            >
              <Calendar className="w-3.5 h-3.5 mr-1.5" />{p.label}
            </Button>
          ))}
          <Button size="sm" variant={showDateRange ? "default" : "outline"}
            onClick={() => setShowDateRange(!showDateRange)} data-testid="filter-custom">
            <Search className="w-3.5 h-3.5 mr-1.5" />Personalizado
          </Button>
          <LojaSelector lojaSelecionada={lojaSelecionada} onLojaChange={setLojaSelecionada} />
          <Button variant="outline" size="sm" onClick={exportarCSV} data-testid="btn-exportar-csv" className="ml-auto">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar
          </Button>
        </div>

        {showDateRange && (
          <div className="flex flex-wrap gap-3 items-end p-4 bg-slate-50 rounded-lg border" data-testid="date-range-picker">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Inicio</label>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
                className="w-40" data-testid="input-data-inicio" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Fim</label>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                className="w-40" data-testid="input-data-fim" />
            </div>
            <Button onClick={aplicarDateRange} size="sm" data-testid="btn-aplicar-datas">Aplicar</Button>
            <Button variant="ghost" size="sm" onClick={() => { setShowDateRange(false); setPeriodo("semana"); }}>Limpar</Button>
          </div>
        )}
      </div>

      {loading ? (
        <LoadingSpinner message="Carregando dados..." />
      ) : (
        <>
          {/* Cards KPI */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stats-cards">
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg" data-testid="card-total-vendas">
              <CardContent className="pt-5 pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Faturamento</p>
                    <p className="text-3xl font-bold mt-1">{fmt(stats?.total_vendas)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="card-total-receitas">
              <CardContent className="pt-5 pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">Vendas</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_receitas || 0}</p>
                  </div>
                  <FileText className="w-8 h-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="card-media-venda">
              <CardContent className="pt-5 pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">Ticket Medio</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{fmt(stats?.media_venda)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Grafico de Faturamento Diario */}
          {stats?.vendas_por_dia?.length > 0 && (
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="chart-vendas">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Faturamento Diario</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={stats.vendas_por_dia}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="data" stroke="#94A3B8" fontSize={11}
                      tickFormatter={(v) => { const d = new Date(v + "T12:00:00"); return `${d.getDate()}/${d.getMonth() + 1}`; }} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmt(v)}
                      contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="valor" stroke="#10B981" strokeWidth={2.5}
                      dot={LINE_DOT_STYLE} activeDot={LINE_ACTIVE_DOT_STYLE} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Pagamentos Pendentes em Aberto */}
          {pendentes && pendentes.quantidade > 0 && (
            <Card className="bg-white border border-amber-200 shadow-sm" data-testid="card-pendentes-abertos">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <CardTitle className="text-base font-semibold text-slate-900">Pagamentos Pendentes em Aberto</CardTitle>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-amber-600">Total a receber ({pendentes.quantidade})</p>
                    <p className="text-xl font-bold text-amber-700" data-testid="pendentes-total">{fmt(pendentes.total_a_receber)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="pendentes-lista">
                  {pendentes.pendentes.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`pendente-${p.id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">
                          {p.nome_cliente}
                          {p.os ? <span className="text-slate-400 font-normal"> · OS {p.os}</span> : null}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <span>{p.nome_loja}</span>
                          {p.telefone_cliente ? (
                            <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.telefone_cliente}</span>
                          ) : null}
                          {p.dias_em_aberto != null && (
                            <span className={p.dias_em_aberto > 30 ? "text-red-500 font-medium" : "text-slate-400"}>
                              {p.dias_em_aberto} dias em aberto
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="font-bold text-amber-700 text-sm whitespace-nowrap">{fmt(p.valor_pendente)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formas de Pagamento - resumo compacto */}
          {stats?.vendas_por_forma_pagamento?.length > 0 && (
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="card-pagamentos">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-base font-semibold text-slate-900">Vendas por Pagamento</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stats.vendas_por_forma_pagamento.map((item, i) => {
                    const pct = stats.total_vendas > 0 ? ((item.valor / stats.total_vendas) * 100).toFixed(0) : 0;
                    return (
                      <div key={item.forma_pagamento} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 text-sm truncate">{item.forma_pagamento}</p>
                          <p className="text-xs text-slate-500">{item.quantidade} vendas ({pct}%)</p>
                        </div>
                        <p className="font-bold text-slate-900 text-sm ml-2 whitespace-nowrap">{fmt(item.valor)}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Origem dos Clientes (canais que mais trazem clientes e faturamento) */}
          {origemCanais?.canais?.length > 0 && (
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="card-origem-canais">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-base font-semibold text-slate-900">Origem dos Clientes</CardTitle>
                </div>
                <p className="text-xs text-slate-500">Qual canal mais traz clientes e mais fatura no periodo</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {origemCanais.canais.map((c, i) => {
                    const maxFat = origemCanais.canais[0]?.faturamento || 1;
                    const pct = maxFat > 0 ? (c.faturamento / maxFat) * 100 : 0;
                    return (
                      <div key={c.origem} data-testid={`origem-canal-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-slate-800 flex items-center gap-2">
                            {i === 0 && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">TOP</span>}
                            {c.origem}
                          </span>
                          <span className="text-slate-500 text-xs">{c.quantidade} clientes · <span className="font-semibold text-slate-900">{fmt(c.faturamento)}</span></span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {lojaSelecionada === "Todas as lojas" && origemCanais.por_loja?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                    {origemCanais.por_loja.map((l) => (
                      <div key={l.loja} className="border border-slate-200 rounded-lg p-3" data-testid={`origem-loja-${l.loja}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-slate-900 text-sm">{l.loja}</span>
                          <span className="text-xs font-semibold text-emerald-700">{fmt(l.faturamento)}</span>
                        </div>
                        {l.origens.map((o) => (
                          <div key={o.origem} className="flex justify-between text-xs text-slate-600 py-0.5">
                            <span>{o.origem}</span>
                            <span className="font-medium">{o.quantidade} · {fmt(o.faturamento)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {(!stats?.vendas_por_dia || stats.vendas_por_dia.length === 0) && (
            <Card className="bg-white border border-slate-200" data-testid="empty-state">
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">Nenhuma venda neste periodo</p>
                <p className="text-xs text-slate-400 mt-1">Tente selecionar outro intervalo de datas</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
